<template>
    <div v-if="peekers.length" class="peek-slot" aria-hidden="true">
        <svg
            v-for="(actor, i) in peekers"
            :key="`peek-${i}`"
            class="peek-sprite"
            :viewBox="actor.viewBox"
            :width="actor.width"
            :height="actor.height"
            :style="actor.style"
            v-html="actor.body"></svg>
    </div>
</template>

<script>
import { SPRITES, castPeekers, spriteAspect } from '../../globals/themeSprites.js'

/**
 * The strip of air above one card, where things hide and pop up.
 *
 * ── Why this isn't part of ThemeScene ────────────────────────────────────
 *
 * Peekers started out rising from the painted horizon, which put them in the
 * scene layer along with the scenery and the weather. That reads as landscape:
 * they belong to the picture *behind* the interface, and at the panel's real
 * height the horizon is a long way from anything you're looking at.
 *
 * Coming up from behind the cards is a different joke — the animals are
 * hiding in the furniture, not in the background — and it can't be done from
 * the scene layer, which has no idea where any card is. So the slot is
 * mounted *by* a card, and gets its geometry for free from being its child.
 *
 * ── How the hiding works ─────────────────────────────────────────────────
 *
 * The slot sits directly on top of its card (`bottom: 100%`) and clips. A
 * sprite is anchored to the slot's bottom edge — which is the card's top edge
 * — and translated down by its own height, putting it entirely outside the
 * clip. Sliding it back to 0 walks it up out from behind the card.
 *
 * The slot is deliberately shorter than the sprites: it's the size of the gap
 * between cards, so a peeker can never rise far enough to overlap the card
 * above it and break the illusion of being *behind* the furniture. You get a
 * head and shoulders, which is what a peek is.
 */
export default {
    name: 'PeekSlot',
    props: {
        theme: { type: Object, required: true },
        // Which card this is. Named `zone` and not `slot` — `slot` is
        // reserved on a Vue 2 component and would be swallowed silently.
        zone: { type: String, required: true },
    },
    computed: {
        peekers() {
            return castPeekers(this.theme.peek, this.zone)
                .filter(spec => SPRITES[spec.sprite])
                .map(spec => {
                    const sprite = SPRITES[spec.sprite]
                    return {
                        viewBox: sprite.vb,
                        body: `<g fill="${spec.color || 'currentColor'}">${sprite.svg}</g>`,
                        width: spec.width,
                        height: Math.round(spec.width * spriteAspect(spec.sprite)),
                        style: {
                            left: spec.left,
                            animationDuration: `${spec.seconds}s`,
                            animationDelay: `${spec.delay}s`,
                        },
                    }
                })
        },
    },
}
</script>

<style scoped lang="scss">
.peek-slot {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    // The gap between cards. Capping the slot here is what guarantees a
    // peeker never climbs over the card above it.
    height: 2rem;
    overflow: hidden;
    pointer-events: none;
}

.peek-sprite {
    position: absolute;
    bottom: 0;
    // Pixel art drawn on whole-unit rects, scaled ~2x. Without this the
    // rect edges land on half-pixels and the whole thing goes slightly soft,
    // which on 1-unit details (an eye, a nostril) is the difference between
    // a face and a smudge.
    shape-rendering: crispEdges;
    // Parked below the clip — i.e. behind the card — until the animation
    // walks it up.
    transform: translateY(100%);
    animation-name: peek-rise;
    animation-iteration-count: infinite;
    // Overshoots a little at the top, so it reads as something popping up
    // rather than a sprite being slid into position.
    animation-timing-function: cubic-bezier(0.34, 1.4, 0.5, 1);
}

// Hidden for roughly three quarters of the cycle, on purpose: a peek should
// be something you happen to catch, not a loop running at you.
@keyframes peek-rise {
    0%,   6%  { transform: translateY(100%); }
    12%, 24%  { transform: translateY(0); }
    30%, 100% { transform: translateY(100%); }
}

@media (prefers-reduced-motion: reduce) {
    .peek-sprite {
        animation: none;
        transform: translateY(0);
    }
}
</style>
